import type { EnvName, ResolvedHosts } from "@/types";

const parseExplicitEnv = (raw: string | null | undefined): EnvName | null => {
  const v = (raw ?? "").trim().toLowerCase();
  if (v === "sit" || v === "uat" || v === "prod") return v;
  return null;
};

/**
 * Resolves Keycloak / account API environment from the loader script `src` hostname
 * (`auth-sit.*` → sit, etc.). **Production IIFE builds ignore `data-env`** so embeds
 * behave exactly as before; in Vite dev only, optional `data-env` on `#md-login`
 * overrides (e.g. `sit` when `src` is localhost).
 */
export const getEnvFromScript = (): EnvName => {
  const script = document.getElementById("md-login") || document.currentScript;
  if (!script) return "prod";

  if (import.meta.env.DEV) {
    const explicit = parseExplicitEnv(
      (script as HTMLScriptElement).getAttribute("data-env"),
    );
    if (explicit) return explicit;
  }

  const src = (script as HTMLScriptElement).getAttribute("src") || "";
  if (!src) return "prod";

  let host = "";
  try {
    host = new URL(src, window.location.href).hostname.toLowerCase();
  } catch {
    host = src.toLowerCase();
  }

  if (host.includes("auth-sit.")) return "sit";
  if (host.includes("auth-uat.")) return "uat";
  if (host === "auth.telekom.gr" || host.startsWith("auth.")) return "prod";
  return "prod";
};

export const resolveEnvHosts = (env: EnvName): ResolvedHosts => {
  const currentHost = (window.location.hostname || "").toLowerCase();
  const parts = currentHost.split(".");
  const domain = parts.length >= 2 ? parts.slice(-2).join(".") : currentHost;

  if (env === "sit") {
    return {
      env,
      domain,
      accountHost: `https://accountsit.${domain}`,
      authHost: `https://auth-sit.${domain}`,
      cosIdHost: "https://accountsit.telekom.gr",
      kcHostUrl: "https://auth-sit.telekom.gr",
    };
  }

  if (env === "uat") {
    return {
      env,
      domain,
      accountHost: `https://accountuat.${domain}`,
      authHost: `https://auth-uat.${domain}`,
      cosIdHost: "https://accountuat.telekom.gr",
      kcHostUrl: "https://auth-uat.telekom.gr",
    };
  }

  return {
    env,
    domain,
    accountHost: `https://account.${domain}`,
    authHost: `https://auth.${domain}`,
    cosIdHost: "https://account.telekom.gr",
    kcHostUrl: "https://auth.telekom.gr",
  };
};
